import Link from 'next/link';

import { Panel, PanelHeader, StatLabel } from './panel';
import { LockedOverlay } from './locked-overlay';
import { ratingToDifficulty } from '@components/fixtures/v2/utils';
import type { DifficultyVariant } from '@components/fixtures/v2/constants';
import { formatNumber, NO_VALUE } from '@lib/page-utils/player-profile/format';

/**
 * Difficulty arrives on the fixture ticker's ten-point scale (higher is
 * harder) and is banded into the same five categories the ticker uses, so a
 * colour means the same thing across the site. The tones are stronger than
 * the ticker's cell pastels because here they render as a thin strip.
 */
const DIFFICULTY_STRIP_COLOUR: Record<DifficultyVariant, string> = {
  'very-easy': 'bg-success-500',
  easy: 'bg-success-300',
  moderate: 'bg-warning-300',
  hard: 'bg-warning-500',
  'very-hard': 'bg-danger-500'
};

const DIFFICULTY_LEGEND_ORDER: DifficultyVariant[] = [
  'very-easy',
  'easy',
  'moderate',
  'hard',
  'very-hard'
];

const UNKNOWN_DIFFICULTY_COLOUR = 'bg-neutral-200';

const DifficultyLegend = () => (
  <span className="flex items-center gap-1.5 text-[11px] text-neutral-500">
    Easier
    <span aria-hidden className="flex overflow-hidden rounded-sm">
      {DIFFICULTY_LEGEND_ORDER.map((variant) => (
        <span
          key={variant}
          className={`h-1.5 w-3 ${DIFFICULTY_STRIP_COLOUR[variant]}`}
        />
      ))}
    </span>
    Harder
  </span>
);

export type UpcomingFixture = {
  gameweek: number;
  /** More than one entry means a double gameweek, stacked in a single cell. */
  matches: {
    opponentShortName: string;
    isHome: boolean;
    difficulty: number | null;
  }[];
  predictedPoints: number | null;
  expectedMinutes: number | null;
};

type UpcomingFixturesProps = {
  fixtures: UpcomingFixture[];
  /** Predictions are auth-gated, so this module locks for free visitors. */
  isRestricted: boolean;
  onUnlockClick: () => void;
};

const FixtureCell = ({
  fixture,
  isRestricted
}: {
  fixture: UpcomingFixture;
  isRestricted: boolean;
}) => {
  const hardestDifficulty = fixture.matches.reduce<number | null>(
    (hardest, match) =>
      match.difficulty === null
        ? hardest
        : Math.max(hardest ?? match.difficulty, match.difficulty),
    null
  );

  return (
    <div className="bg-white">
      <div
        aria-hidden
        className={`h-1.5 ${
          hardestDifficulty === null
            ? UNKNOWN_DIFFICULTY_COLOUR
            : DIFFICULTY_STRIP_COLOUR[ratingToDifficulty(hardestDifficulty)]
        }`}
      />
      <div className="px-4 pt-4 pb-4">
        <StatLabel>GW {fixture.gameweek}</StatLabel>
        <div className="mt-1.5 flex flex-col gap-0.5">
          {fixture.matches.map((match) => (
            <p
              key={`${match.opponentShortName}-${match.isHome}`}
              className="font-sans text-[19px] font-bold leading-tight text-neutral-900"
            >
              {match.opponentShortName}
              <span className="text-xs font-semibold text-neutral-400">
                {match.isHome ? ' (H)' : ' (A)'}
              </span>
            </p>
          ))}
        </div>

        <p className="mt-3 font-sans text-[27px] font-bold tabular-nums leading-none text-neutral-900">
          {isRestricted ? NO_VALUE : formatNumber(fixture.predictedPoints, 1)}
        </p>
        <StatLabel className="mt-1">Pred pts</StatLabel>
        <p className="mt-2 text-xs text-neutral-700">
          {isRestricted || fixture.expectedMinutes === null
            ? `${NO_VALUE} xMins`
            : `${Math.round(fixture.expectedMinutes)} xMins`}
        </p>
      </div>
    </div>
  );
};

export const UpcomingFixtures = ({
  fixtures,
  isRestricted,
  onUnlockClick
}: UpcomingFixturesProps) => (
  <Panel className="overflow-hidden">
    <PanelHeader
      title="Next six gameweeks"
      subtitle="Predicted points and expected minutes from the Hub model."
      action={
        <div className="flex flex-wrap items-center justify-end gap-x-4 gap-y-2">
          <DifficultyLegend />
          <Link
            href="/fixture-ticker"
            className="text-[13px] font-semibold text-primary-600 hover:underline"
          >
            Fixture analyser →
          </Link>
        </div>
      }
    />
    <div className="relative border-t border-neutral-200">
      <div className="grid grid-cols-2 gap-px bg-neutral-200 md:grid-cols-3 lg:grid-cols-6">
        {fixtures.map((fixture) => (
          <FixtureCell
            key={fixture.gameweek}
            fixture={fixture}
            isRestricted={isRestricted}
          />
        ))}
      </div>
      {isRestricted && (
        <LockedOverlay
          title="Predicted points are a member feature"
          message="See the Hub model's predicted points and expected minutes for every upcoming fixture."
          onUnlockClick={onUnlockClick}
        />
      )}
    </div>
  </Panel>
);
